import React, { CSSProperties, useCallback, useEffect, useRef } from 'react';
import styles from './ScaledText.module.css';

/**
 * ScaledText component that automatically adjusts text size to fit within its container.
 *
 * This component measures the text width and container width, then scales the text down
 * using CSS transforms if it overflows the container. The scaling maintains aspect ratio
 * and prevents text from being cut off or wrapping.
 *
 * @example
 * ```tsx
 * <ScaledText
 *   text="Order #12345"
 *   fontSize="48px"
 *   className="order-number"
 * />
 * ```
 */
interface ScaledTextProps {
  /** The text content to display and scale */
  text: string;
  /** Additional CSS classes to apply to the text element */
  className?: string;
  /** Base font size before any scaling is applied */
  fontSize: CSSProperties['fontSize'];
  // Optional test ID for data-testid attribute
  testid?: string;
}

/**
 * ScaledText - A responsive text component that automatically scales to fit its container
 *
 * Features:
 * - Automatically scales text down when it overflows the container width
 * - Preserves original font size when text fits naturally
 * - Uses CSS transforms for smooth scaling without affecting layout
 * - Re-calculates scaling when text content changes
 * - Maintains text readability by scaling proportionally
 */
export const ScaledText: React.FC<ScaledTextProps> = ({
  text,
  className = '',
  fontSize,
  testid = 'scaled-text',
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const prevScaleRef = useRef<number | null>(null);

  const calculateScale = useCallback(() => {
    const wrapper = wrapperRef.current;
    const textElement = textRef.current;

    if (wrapper && textElement) {
      const wrapperWidth = wrapper.clientWidth;
      const textWidth = textElement.scrollWidth;

      let scale = 1;
      if (textWidth > wrapperWidth) {
        scale = wrapperWidth / textWidth;
      }
      if (prevScaleRef.current !== scale) {
        textElement.style.transform = `scale(${scale})`;
        prevScaleRef.current = scale;
      }
    }
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const resizeObserver = new ResizeObserver(calculateScale);
    if (wrapper) {
      resizeObserver.observe(wrapper);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateScale]);

  useEffect(() => {
    calculateScale();
  }, [calculateScale]);

  return (
    <div ref={wrapperRef} className={styles.scaledTextWrapper}>
      <div
        data-testid={testid}
        ref={textRef}
        className={`${styles.scaledText} ${className}`}
        style={{ fontSize }}
      >
        {text}
      </div>
    </div>
  );
};
